import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import useUserStore from '../store/userStore';
import authService from '../services/authService';

function UserMenu({ user }) {
  const router = useRouter();
  const { isSubscriptionActive } = useUserStore();
  const [isOpen, setIsOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const menuRef = useRef(null);
  
  // Handle clicks outside to close menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    // Only add the event listener when the menu is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Close menu with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);
  
  // Toggle menu open/closed
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Handle logout - используем централизованный метод
  const handleLogout = async (e) => {
    e.preventDefault();
    
    if (loggingOut) return; // Предотвращаем двойной клик
    
    try {
      setLoggingOut(true);
      console.log('🔴 Начинаем процесс выхода из системы через централизованный logout');
      
      // Закрываем меню
      setIsOpen(false);
      
      // Используем централизованный метод logout, который выполнит все необходимые действия
      await authService.unifiedLogout({
        clearCart: true,
        notifyServer: true,
        reload: true
      });
      
    } catch (error) {
      console.error('❌ Ошибка при выходе из системы:', error);
      
      // Если произошла ошибка, всё равно принудительно перезагружаем страницу
      window.location.reload();
    } finally {
      setLoggingOut(false);
    }
  };

  // Handle menu item click with navigation
  const handleMenuItemClick = (path) => {
    router.push(path);
    setIsOpen(false);
  };

  // Handle subscription management - умная маршрутизация
  const handleManageSubscription = async () => {
    try {
      // Отправляем событие клика на кнопку управления подпиской
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'subscription_button_click',
          'action_type': 'manage',
          'userID': useUserStore.getState().user?.id || '',
          'current_subscription': useUserStore.getState().subscription || 'none'
        });
      }
      
      setLoadingSubscription(true);
      setIsOpen(false);
      
      // Если у пользователя уже есть подписка, ведем на Stripe Portal
      if (isSubscriptionActive) {
        const response = await useUserStore.getState().fetchWithTokenRefresh(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/portal`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ return_url: window.location.href }),
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to get customer portal URL');
        }
        
        const data = await response.json();
        
        // Redirect to Stripe Customer Portal
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error('Customer portal URL is missing in the response');
        }
      } else {
        // Если подписки нет, ведем на страницу тарифов
        router.push('/pricing');
      }
    } catch (err) {
      console.error('Portal URL error:', err);
      alert('Failed to access subscription management. Please try again.');
    } finally {
      setLoadingSubscription(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar/Toggle Button */}
      <img 
        src={user?.photoURL || '/images/default-avatar.svg'} 
        alt="User Avatar" 
        className="w-10 h-10 rounded-full object-cover border border-gray-200 cursor-pointer"
        onClick={toggleMenu}
      />
      
      {/* Dropdown Menu - увеличен z-index с z-50 до z-[70] */}
      {isOpen && (
        <div className="absolute right-0 top-12 w-64 bg-white shadow-lg rounded-xl py-3 z-[70]">
          {/* User info */}
          <div className="px-6 py-3 border-b border-gray-100 rounded-t-xl">
            <div className="truncate">
              <p className="text-base font-medium text-gray-800">
                {user?.displayName || user?.email || 'User'}
              </p>
              {user?.email && user?.displayName && (
                <p className="text-sm text-gray-500 truncate">{user.email}</p>
              )}
            </div>
          </div>
          
          {/* Menu items */}
          <div className="mt-2">            
            <MenuItem onClick={() => handleMenuItemClick('/orders')}>
              My Orders
            </MenuItem>
            
            {/* Показываем Manage Subscription только при наличии активной подписки */}
            {isSubscriptionActive && (
              <MenuItem onClick={handleManageSubscription} disabled={loadingSubscription}>
                {loadingSubscription ? 'Loading...' : 'Manage Subscription'}
              </MenuItem>
            )}
            
            {/* Показываем Pricing в меню только когда его нет в хедере */}
            <div className="block lg:hidden">
              <MenuItem onClick={() => handleMenuItemClick('/pricing')}>
                Pricing
              </MenuItem>
            </div>
            
            <MenuItem onClick={handleLogout} disabled={loggingOut}>
              {loggingOut ? 'Logging out...' : 'Logout'}
            </MenuItem>
          </div>
        </div>
      )}
    </div>
  );
}

// Menu item component
function MenuItem({ children, onClick, disabled = false }) {
  return (
    <button
      className={`w-full text-left px-6 py-3 text-base text-gray-700 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 hover:rounded-lg'
      } transition duration-150 rounded-lg`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export default UserMenu;